import { ResponsiveLineCanvas } from "@nivo/line";
import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand } from "@fortawesome/free-solid-svg-icons";
import ErrorScreen from "../../../../components/error/ErrorScreen";
import getDataFromApi from "../../../../components/getDataFromApi";
import Loader from "../../../../components/Loader";
import { CloseIcon } from "../../../Common/SvgIcons/WorkflowIcons";
import NivoTextParser from "./NivoTextParser";
import nivoTheme from "./nivoTheme";

const FALLBACK_COLORS = ["#2673e8", "#25a0c7", "#6f7ee8", "#35a55f", "#e19b2d"];
const CARD_MAX_TOTAL_POINTS = 1800;
const MODAL_MAX_TOTAL_POINTS = 3600;
const MIN_POINTS_PER_SERIES = 60;
const CARD_MAX_X_TICKS = 14;
const MODAL_MAX_X_TICKS = 32;
const POINTS_VISIBLE_LIMIT = 160;
const TOOLTIP_WIDTH = 240;

const getCssVariable = (name, fallback) => {
  if (typeof window === "undefined") return fallback;

  return (
    getComputedStyle(document.documentElement).getPropertyValue(name).trim() ||
    fallback
  );
};

const getLineTheme = (_themeKey) => {
  const textColor = getCssVariable("--text-color", "#1f2937");
  const gridColor = getCssVariable("--glass-color", "#e5e7eb");

  return {
    ...nivoTheme,
    background: "transparent",
    text: {
      fill: textColor,
    },
    axis: {
      domain: {
        line: {
          stroke: gridColor,
          strokeWidth: 1,
        },
      },
      ticks: {
        line: {
          stroke: textColor,
        },
        text: {
          fill: textColor,
          fontSize: 11,
          fontWeight: 700,
        },
      },
      legend: {
        text: {
          fill: textColor,
          fontSize: 12,
          fontWeight: 700,
        },
      },
    },
    grid: {
      line: {
        stroke: gridColor,
        strokeWidth: 1,
        strokeDasharray: "4 4",
      },
    },
    labels: {
      text: {
        fill: textColor,
      },
    },
    legends: {
      text: {
        fill: textColor,
      },
    },
  };
};

const getNumber = (value, fallback = 0) => {
  if (value instanceof Date) return value.getTime();

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getSeriesLimit = (seriesCount, maxTotalPoints) =>
  Math.max(
    MIN_POINTS_PER_SERIES,
    Math.floor(maxTotalPoints / Math.max(seriesCount, 1))
  );

const downsampleData = (data, threshold) => {
  if (!Array.isArray(data) || data.length <= threshold || threshold < 3) {
    return data;
  }

  const sampled = [data[0]];
  const bucketSize = (data.length - 2) / (threshold - 2);
  let anchorIndex = 0;

  for (let bucketIndex = 0; bucketIndex < threshold - 2; bucketIndex += 1) {
    const avgRangeStart = Math.floor((bucketIndex + 1) * bucketSize) + 1;
    const avgRangeEnd = Math.min(
      Math.floor((bucketIndex + 2) * bucketSize) + 1,
      data.length
    );
    const avgRangeLength = Math.max(avgRangeEnd - avgRangeStart, 1);

    let avgX = 0;
    let avgY = 0;

    for (let index = avgRangeStart; index < avgRangeEnd; index += 1) {
      avgX += getNumber(data[index]?.x, index);
      avgY += getNumber(data[index]?.y);
    }

    avgX /= avgRangeLength;
    avgY /= avgRangeLength;

    const rangeStart = Math.floor(bucketIndex * bucketSize) + 1;
    const rangeEnd = Math.min(
      Math.floor((bucketIndex + 1) * bucketSize) + 1,
      data.length - 1
    );
    const anchorX = getNumber(data[anchorIndex]?.x, anchorIndex);
    const anchorY = getNumber(data[anchorIndex]?.y);

    let maxArea = -1;
    let nextIndex = rangeStart;

    for (let index = rangeStart; index < rangeEnd; index += 1) {
      const currentX = getNumber(data[index]?.x, index);
      const currentY = getNumber(data[index]?.y);
      const area = Math.abs(
        (anchorX - avgX) * (currentY - anchorY) -
        (anchorX - currentX) * (avgY - anchorY)
      );

      if (area > maxArea) {
        maxArea = area;
        nextIndex = index;
      }
    }

    sampled.push(data[nextIndex]);
    anchorIndex = nextIndex;
  }

  sampled.push(data[data.length - 1]);
  return sampled;
};

const downsampleGraphData = (seriesList, maxTotalPoints) => {
  const seriesLimit = getSeriesLimit(seriesList.length, maxTotalPoints);

  return seriesList.map((series) => ({
    ...series,
    data: downsampleData(series.data, seriesLimit),
  }));
};

const getLongestSeriesData = (seriesList) =>
  seriesList.reduce(
    (longest, series) =>
      (series?.data?.length ?? 0) > longest.length ? series.data : longest,
    []
  );

const getSparseTicks = (seriesList, maxTicks) => {
  const referenceData = getLongestSeriesData(seriesList);
  const values = referenceData
    .map((point) => point?.x)
    .filter((value) => value !== undefined && value !== null);

  if (values.length <= maxTicks) return values;

  const step = Math.ceil((values.length - 1) / Math.max(maxTicks - 1, 1));
  const ticks = values.filter((_, index) => index % step === 0);
  const lastValue = values[values.length - 1];

  if (ticks[ticks.length - 1] !== lastValue) ticks.push(lastValue);
  return ticks;
};

const formatAxisTick = (value) => {
  const label =
    value instanceof Date ? value.toLocaleDateString() : String(value);

  if (label.length <= 14) return label;
  return `${label.slice(0, 6)}...${label.slice(-4)}`;
};

const formatAxisLeftValue = (value) => {
  if (typeof value !== "number") return value;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return Number.isInteger(value) ? value : Number(value.toFixed(2));
};

const formatTooltipValue = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value !== "number") return value;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(2)}k`;
  return Number.isInteger(value) ? value : Number(value.toFixed(4));
};

const formatTooltipValueWithUnit = (value, unit) => {
  const formattedValue = formatTooltipValue(value);
  const normalizedUnit = String(unit || "").trim();

  if (!normalizedUnit) return formattedValue;
  if (["%", "°"].includes(normalizedUnit)) {
    return `${formattedValue}${normalizedUnit}`;
  }

  return `${formattedValue} ${normalizedUnit}`;
};

const getNearestPointByX = (data, targetX, fallbackIndex) => {
  if (!Array.isArray(data) || !data.length) return null;

  let nearestPoint = data[Math.min(fallbackIndex, data.length - 1)] ?? data[0];
  let nearestDistance = Number.POSITIVE_INFINITY;

  data.forEach((point, index) => {
    const distance = Math.abs(getNumber(point?.x, index) - targetX);

    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestPoint = point;
    }
  });

  return nearestPoint;
};

const getHoverInfo = (event, data, margin, scale) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const innerWidth = rect.width - margin.left - margin.right;
  const innerHeight = rect.height - margin.top - margin.bottom;

  if (innerWidth <= 0 || innerHeight <= 0) return null;

  const mouseX = event.clientX - rect.left;
  const mouseY = event.clientY - rect.top;

  if (
    mouseX < margin.left ||
    mouseX > rect.width - margin.right ||
    mouseY < margin.top ||
    mouseY > rect.height - margin.bottom
  ) {
    return null;
  }

  const boundedX = Math.min(Math.max(mouseX - margin.left, 0), innerWidth);
  const ratio = innerWidth ? boundedX / innerWidth : 0;
  const referenceData = getLongestSeriesData(data);

  if (!referenceData.length) return null;

  const fallbackIndex = Math.min(
    Math.round(ratio * (referenceData.length - 1)),
    referenceData.length - 1
  );
  const isLinearScale = scale === "linear";
  const referenceValues = referenceData.map((point, index) =>
    getNumber(point?.x, index)
  );
  const minX = Math.min(...referenceValues);
  const maxX = Math.max(...referenceValues);
  const targetX =
    isLinearScale && Number.isFinite(minX) && Number.isFinite(maxX)
      ? minX + ratio * (maxX - minX)
      : getNumber(referenceData[fallbackIndex]?.x, fallbackIndex);

  const points = data
    .map((series) => {
      const point = isLinearScale
        ? getNearestPointByX(series.data, targetX, fallbackIndex)
        : series.data?.[Math.min(fallbackIndex, series.data.length - 1)];

      if (!point) return null;

      return {
        id: series.id,
        color: series.color ?? "#1a73e8",
        x: point.x,
        y: point.y,
      };
    })
    .filter(Boolean);

  if (!points.length) return null;

  return {
    left: Math.min(mouseX + 12, Math.max(8, rect.width - TOOLTIP_WIDTH)),
    top: Math.min(mouseY + 12, Math.max(8, rect.height - 96)),
    crosshairX: margin.left + boundedX,
    chartTop: margin.top,
    chartHeight: innerHeight,
    x: points[0].x,
    points,
  };
};

export default function LineNivo({
  selectedData,
  expQuery,
  chartType,
  chartParameter,
  singleGraphData,
  scale,
  xLabel,
  yLabel,
  xUnit,
  yUnit,
  title,
  height,
  cssManage,
  showLegend = false,
}) {
  const [graphData, setGraphData] = useState();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hoverInfo, setHoverInfo] = useState(null);
  const hasSingleGraphData = Boolean(singleGraphData?.length);

  const normalizedGraphData = useMemo(() => {
    if (!Array.isArray(graphData)) return [];

    return graphData.map((item, index) => ({
      ...item,
      color: item.color ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length],
      data: Array.isArray(item.data) ? item.data : [],
    }));
  }, [graphData]);

  const modalChartData = useMemo(
    () => downsampleGraphData(normalizedGraphData, MODAL_MAX_TOTAL_POINTS),
    [normalizedGraphData]
  );

  const chartData = useMemo(
    () => downsampleGraphData(modalChartData, CARD_MAX_TOTAL_POINTS),
    [modalChartData]
  );

  const xScale = useMemo(() => ({ type: scale ?? "point" }), [scale]);
  const themeKey =
    typeof document === "undefined"
      ? "light"
      : document.documentElement.getAttribute("data-theme") || "light";
  const chartTheme = useMemo(() => getLineTheme(themeKey), [themeKey]);

  useEffect(() => {
    if (hasSingleGraphData) return;

    setGraphData();

    if (chartParameter?.split("?")[0] !== undefined) {
      const url =
        `${window["ENV"].API_URL}/experiments/main/chart/${chartType}/${chartParameter}` +
        expQuery;

      setLoading(true);

      getDataFromApi(url, "GET")
        .then((result) => {
          setGraphData(result);
        })
        .catch((e) => console.log(e))
        .finally(() => setLoading(false));
    }
  }, [chartParameter, chartType, expQuery, hasSingleGraphData]);

  useEffect(() => {
    if (selectedData) {
      setGraphData((currentGraphData) => {
        if (!currentGraphData) return currentGraphData;

        return currentGraphData.map((item) => ({
          ...item,
          color:
            selectedData.find(
              (selectedItem) => selectedItem.exp_name === item.id
            )?.color ?? item.color,
        }));
      });
    }
  }, [selectedData]);

  useEffect(() => {
    setGraphData(singleGraphData);
  }, [singleGraphData]);

  if (loading) return <Loader />;

  if (!normalizedGraphData.length) {
    return <ErrorScreen message="No graph Found" />;
  }

  const renderLegend = (data) => showLegend && data.length > 0 && (
    <aside className="evaluation-analytics-side-legend" aria-label={`${title || "Line chart"} legend`}>
      <div className="evaluation-analytics-side-legend__items">
        {data.map((series) => (
          <span className="evaluation-analytics-side-legend__item" key={series.id} title={String(series.id)}>
            <i style={{ backgroundColor: series.color }} />
            <span>{String(series.id).replaceAll("_", " ")}</span>
          </span>
        ))}
      </div>
    </aside>
  );

  const renderChart = (data, maxXTicks) => {
    const xTickValues = getSparseTicks(data, maxXTicks);
    const maxSeriesLength = Math.max(
      ...data.map((series) => series?.data?.length ?? 0),
      0
    );
    const shouldRotateTicks =
      xTickValues.length > 8 ||
      xTickValues.some((value) => String(value).length > 8);
    const margin = {
      top: 18,
      right: 20,
      bottom: shouldRotateTicks ? 88 : 66,
      left: yLabel ? 84 : 60,
    };

    return (
      <div
        style={{ height: "100%", position: "relative" }}
        onMouseMove={(event) =>
          setHoverInfo(getHoverInfo(event, data, margin, scale))
        }
        onMouseLeave={() => setHoverInfo(null)}
      >
        <ResponsiveLineCanvas
          data={data}
          margin={margin}
          xScale={xScale}
          yScale={{
            type: "linear",
            stacked: false,
          }}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            orient: "bottom",
            tickValues: xTickValues,
            tickSize: 0,
            tickPadding: 12,
            tickRotation: shouldRotateTicks ? -35 : 0,
            legend: xLabel,
            legendOffset: shouldRotateTicks ? 68 : 46,
            legendPosition: "middle",
            format: formatAxisTick,
          }}
          axisLeft={{
            legend: yLabel,
            legendOffset: yLabel ? -64 : -46,
            legendPosition: "middle",
            format: formatAxisLeftValue,
            tickSize: 0,
            tickPadding: 10,
          }}
          colors={(serie) => serie.color ?? "#1a73e8"}
          theme={chartTheme}
          enablePoints={
            Boolean(scale) && maxSeriesLength <= POINTS_VISIBLE_LIMIT
          }
          pointSize={5}
          pointColor={{ from: "color", modifiers: [["opacity", 0.3]] }}
          pointBorderWidth={2}
          pointBorderColor={{ from: "color" }}
          enableArea={true}
          areaOpacity={0.18}
          gridXValues={xTickValues}
          enableGridX={true}
          enableGridY={true}
          isInteractive={false}
          legends={[]}
        />

        {hoverInfo && (
          <>
            <div
              style={{
                position: "absolute",
                top: hoverInfo.chartTop,
                left: hoverInfo.crosshairX,
                height: hoverInfo.chartHeight,
                borderLeft: "1px dashed var(--glass-color)",
                pointerEvents: "none",
              }}
            />

            <div
              className="shadow"
              style={{
                position: "absolute",
                left: hoverInfo.left,
                top: hoverInfo.top,
                minWidth: 220,
                maxWidth: 280,
                padding: "10px 12px",
                backgroundColor: "var(--bg-color8)",
                border: "1px solid var(--glass-color)",
                borderRadius: 6,
                color: "var(--text-color)",
                fontSize: 12,
                pointerEvents: "none",
                zIndex: 5,
                boxSizing: "border-box",
              }}
            >
              {xLabel ?? "X"}: <b>{formatTooltipValueWithUnit(hoverInfo.x, xUnit)}</b>
              <br />

            
              {hoverInfo.points.map((point) => (
                <div
                  key={`${point.id}-${point.x}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 4,
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      minWidth: 10,
                      borderRadius: 2,
                      background: point.color,
                      display: "inline-block",
                    }}
                  />
                  <span style={{ whiteSpace: "normal" }}>
                    {point.id}: <b>{formatTooltipValueWithUnit(point.y, yUnit)}</b>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className={`chart ${!cssManage ? "badge-border p-4" : ""}`}
        style={{ height: height ?? "400px", position: "relative", cursor: "pointer" }}
        role="button"
        tabIndex={0}
        aria-label={`Expand ${title || "line chart"}`}
        onClick={() => {
          setHoverInfo(null);
          setIsModalOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setHoverInfo(null);
            setIsModalOpen(true);
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setHoverInfo(null);
        }}
      >
        {title && <NivoTextParser title={title} />}

        {isHovered && (
          <div
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              cursor: "pointer",
              zIndex: 3,
            }}
            onClick={(event) => {
              event.stopPropagation();
              setHoverInfo(null);
              setIsModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faExpand} title="Expand" />
          </div>
        )}

        <div
          style={{
            height: title ? "calc(100% - 52px)" : "100%",
            position: "relative",
          }}
        >
          {showLegend ? <div className="evaluation-analytics-line-layout has-legend">
            <div className="evaluation-analytics-line-canvas">
              {renderChart(chartData, CARD_MAX_X_TICKS)}
            </div>
            {renderLegend(chartData)}
          </div> : renderChart(chartData, CARD_MAX_X_TICKS)}
        </div>
      </div>

      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 6,
          }}
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="pt-0 pe-0 container-card w-75 w-md-100"
            style={{
              backgroundColor: "var(--bg-color8)",
              padding: "50px",
              height: "70%",
              position: "relative",
              overflow: "visible",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close expanded chart"
              className="plain-button text-danger"
              onClick={() => setIsModalOpen(false)}
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                cursor: "pointer",
                zIndex: 4,
              }}
            >
              <CloseIcon className="wf-icon text-secondary" />
            </button>

            <div className="p-3" style={{ width: "100%", height: "100%" }}>
              {title && (
                <div style={{ height: "50px" }}>
                  <NivoTextParser title={title} />
                </div>
              )}

              <div
                style={{
                  height: title ? "calc(100% - 50px)" : "100%",
                  position: "relative",
                }}
              >
                {showLegend ? <div className="evaluation-analytics-line-layout has-legend">
                  <div className="evaluation-analytics-line-canvas">
                    {renderChart(modalChartData, MODAL_MAX_X_TICKS)}
                  </div>
                  {renderLegend(modalChartData)}
                </div> : renderChart(modalChartData, MODAL_MAX_X_TICKS)}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
